import { useCallback, useRef, useSyncExternalStore } from "react";
import type { Page } from "../page";
import { pagesCollection } from "./pages-collection";

/** Reads pages straight off the collection instead of through
 * `useLiveQuery`. The capture screen mutates a page's row several times in
 * quick succession (insert, hash patch, upload-queue claim, outcome), and
 * any `useLiveQuery` subscribed to this collection — orderBy or not — can
 * throw "Query contributors with the same row key are not congruent"
 * while settling those writes (TanStack/db#1767, #1783). That throw isn't
 * just a console warning: it corrupts the collection's own optimistic/
 * synced state, so the row can vanish from every reader, including other,
 * unrelated `useLiveQuery` subscriptions on this same collection. The raw
 * collection (`toArray`/`subscribeChanges`) doesn't go through that
 * differential query engine and stays correct through the same cascade —
 * so nothing in this app may subscribe to `pagesCollection` via
 * `useLiveQuery`; read through one of these hooks instead. */
function usePagesWhere(scriptId: string | null): Page[] {
  const cacheRef = useRef<{ scriptId: string | null; pages: Page[] } | null>(null);

  const getSnapshot = useCallback((): Page[] => {
    if (cacheRef.current && cacheRef.current.scriptId === scriptId) {
      return cacheRef.current.pages;
    }
    const all = pagesCollection.toArray;
    const pages = (scriptId === null ? all : all.filter(p => p.scriptId === scriptId)).sort(
      (a, b) => a.pageIndex - b.pageIndex,
    );
    cacheRef.current = { scriptId, pages };
    return pages;
  }, [scriptId]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = pagesCollection.subscribeChanges(() => {
      cacheRef.current = null;
      onStoreChange();
    });
    return () => subscription.unsubscribe();
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot);
}

export function useScriptPages(scriptId: string): Page[] {
  return usePagesWhere(scriptId);
}

export function useAllPages(): Page[] {
  return usePagesWhere(null);
}
